/**
 * For Ninetailed implementation refers to [Ninetailed Examples](https://github.com/ninetailed-inc/ninetailed-examples/tree/main/marketing-contentful-next-app-all-in).
 * This component is used to track the visibility of a component and send it to Ninetailed.
 */

import { useEffect, useRef } from 'react'
import { ExperienceConfiguration } from '@ninetailed/experience.js'
import { Reference } from '@ninetailed/experience.js-shared'
import { useNinetailed } from '@ninetailed/experience.js-react'
import { noop } from '@lib/utils/noop'
import { ComponentMarker } from './component-marker'

export function ComponentTracker<Variant extends Reference>({
  experience,
  variant,
  variantIndex,
}: {
  experience?: ExperienceConfiguration<Variant>
  variant: Variant
  variantIndex: number
}) {
  const componentRef = useRef<Element | null>(null)
  const ninetailed = useNinetailed()

  useEffect(() => {
    const componentElement = componentRef.current

    if (componentElement && !(componentElement instanceof Element)) {
      const isObject = typeof componentElement === 'object' && componentElement !== null
      const constructorName = isObject
        ? // eslint-disable-next-line
          ((componentElement as any).constructor.name as string)
        : ''
      const isConstructorNameNotObject = constructorName && constructorName !== 'Object'

      console.warn(
        `The component ref being in Experience is an invalid element. Expected an Element but got ${typeof componentElement}${
          isConstructorNameNotObject ? ` of type ${constructorName}` : ''
        }. This component won't be observed.`
      )

      return () => {
        // noop
      }
    }

    if (componentElement) {
      ninetailed.observeElement({
        // #ToDo Review to double-check that we actually use Entry here
        componentType: 'Entry',
        element: componentElement,
        experience,
        audience: experience?.audience,
        variant: 'hidden' in variant && variant.hidden ? { ...variant, id: `${variant.id}-hidden` } : variant,
        variantIndex,
      })

      return () => {
        if (componentElement) {
          ninetailed.unobserveElement(componentElement)
        }
      }
    }

    return noop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(experience), JSON.stringify(variant), variantIndex])

  return <ComponentMarker ref={componentRef}></ComponentMarker>
}
